import React, { useState } from 'react';
import { v1 as uuidv1, v4 as uuidv4, v5 as uuidv5, v6 as uuidv6, v7 as uuidv7 } from 'uuid';
import ToolLayout from '../components/ToolLayout';

type UuidVersion = 'v1' | 'v4' | 'v5' | 'v6' | 'v7';

interface GeneratedUuid {
  id: string;
  uuid: string;
  version: UuidVersion;
  timestamp: string;
}

const UuidGenerator: React.FC = () => {
  const [selectedVersion, setSelectedVersion] = useState<UuidVersion>('v4');
  const [quantity, setQuantity] = useState<number>(1);
  const [generatedUuids, setGeneratedUuids] = useState<GeneratedUuid[]>([]);
  const [copySuccess, setCopySuccess] = useState<string>('');

  // V5 namespace and name for demonstration
  const [v5Namespace, setV5Namespace] = useState<string>('6ba7b810-9dad-11d1-80b4-00c04fd430c8');
  const [v5Name, setV5Name] = useState<string>('example.com');

  const generateUuid = (version: UuidVersion): string => {
    switch (version) {
      case 'v1':
        return uuidv1();
      case 'v4':
        return uuidv4();
      case 'v5':
        return uuidv5(v5Name, v5Namespace);
      case 'v6':
        return uuidv6();
      case 'v7':
        return uuidv7();
      default:
        return uuidv4();
    }
  };

  const handleGenerate = () => {
    const newUuids: GeneratedUuid[] = [];
    const count = Math.min(Math.max(1, quantity), 100); // Limit to 100

    for (let i = 0; i < count; i++) {
      newUuids.push({
        id: `uuid-${Date.now()}-${i}`,
        uuid: generateUuid(selectedVersion),
        version: selectedVersion,
        timestamp: new Date().toLocaleString()
      });
    }

    setGeneratedUuids(newUuids);
    setCopySuccess('');
  };

  const handleCopy = (uuid: string) => {
    navigator.clipboard.writeText(uuid).then(() => {
      setCopySuccess(uuid);
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const handleCopyAll = () => {
    const allUuids = generatedUuids.map(item => item.uuid).join('\n');
    navigator.clipboard.writeText(allUuids).then(() => {
      setCopySuccess('all');
      setTimeout(() => setCopySuccess(''), 2000);
    });
  };

  const handleClear = () => {
    setGeneratedUuids([]);
    setCopySuccess('');
  };

  const versionInfo = {
    v1: {
      title: 'UUID v1',
      description: 'Time-based UUID using timestamp and MAC address',
      note: 'Contains timestamp and potentially identifiable information'
    },
    v4: {
      title: 'UUID v4',
      description: 'Random UUID (most commonly used)',
      note: 'Purely random, no identifiable information'
    },
    v5: {
      title: 'UUID v5',
      description: 'Name-based UUID using SHA-1 hash',
      note: 'Deterministic - same input always produces same UUID'
    },
    v6: {
      title: 'UUID v6',
      description: 'Reordered time-based UUID (improved v1)',
      note: 'Time-ordered with better database indexing'
    },
    v7: {
      title: 'UUID v7',
      description: 'Unix timestamp-based UUID',
      note: 'Time-ordered with millisecond precision'
    }
  };

  return (
    <ToolLayout
      title="UUID Generator"
      description="Generate UUIDs (Universally Unique Identifiers) with support for multiple versions"
    >
      <div className="uuid-generator">
        {/* Version Selection */}
        <div className="input-group">
          <label className="input-label">UUID Version</label>
          <div className="uuid-version-grid">
            {(Object.keys(versionInfo) as UuidVersion[]).map((version) => (
              <button
                key={version}
                className={`version-button ${selectedVersion === version ? 'active' : ''}`}
                onClick={() => setSelectedVersion(version)}
              >
                <div className="version-title">{versionInfo[version].title}</div>
                <div className="version-description">{versionInfo[version].description}</div>
              </button>
            ))}
          </div>
          <p className="version-note">
            <strong>Note:</strong> {versionInfo[selectedVersion].note}
          </p>
        </div>

        {/* V5 Options */}
        {selectedVersion === 'v5' && (
          <div className="v5-options">
            <div className="input-group">
              <label className="input-label">Namespace UUID</label>
              <input
                type="text"
                className="input-field"
                value={v5Namespace}
                onChange={(e) => setV5Namespace(e.target.value)}
                placeholder="Enter namespace UUID"
              />
            </div>
            <div className="input-group">
              <label className="input-label">Name</label>
              <input
                type="text"
                className="input-field"
                value={v5Name}
                onChange={(e) => setV5Name(e.target.value)}
                placeholder="Enter name"
              />
            </div>
          </div>
        )}

        {/* Quantity Selection */}
        <div className="input-group">
          <label className="input-label">
            Number of UUIDs to Generate (1-100)
          </label>
          <input
            type="number"
            className="input-field"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            min="1"
            max="100"
          />
        </div>

        {/* Action Buttons */}
        <div className="button-group">
          <button className="action-button" onClick={handleGenerate}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
              <path d="M21 3v5h-5"/>
            </svg>
            Generate UUIDs
          </button>
          {generatedUuids.length > 0 && (
            <>
              <button className="action-button secondary" onClick={handleCopyAll}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                Copy All
              </button>
              <button className="action-button secondary" onClick={handleClear}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Clear
              </button>
            </>
          )}
        </div>

        {/* Copy Success Message */}
        {copySuccess && (
          <div className="success-message">
            {copySuccess === 'all' ? 'All UUIDs copied to clipboard!' : 'UUID copied to clipboard!'}
          </div>
        )}

        {/* Generated UUIDs Display */}
        {generatedUuids.length > 0 && (
          <div className="uuid-results">
            <div className="results-header">
              <h3 className="results-title">Generated UUIDs ({generatedUuids.length})</h3>
            </div>
            <div className="uuid-list">
              {generatedUuids.map((item) => (
                <div key={item.id} className="uuid-item">
                  <div className="uuid-content">
                    <code className="uuid-value">{item.uuid}</code>
                    <span className="uuid-meta">
                      {item.version.toUpperCase()} • {item.timestamp}
                    </span>
                  </div>
                  <button
                    className="copy-button"
                    onClick={() => handleCopy(item.uuid)}
                    title="Copy to clipboard"
                  >
                    {copySuccess === item.uuid ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="info-section">
          <h3 className="info-title">About UUIDs</h3>
          <ul className="info-list">
            <li><strong>UUID v1:</strong> Based on timestamp and MAC address. Useful when you need time-ordered IDs but may expose machine information.</li>
            <li><strong>UUID v4:</strong> Randomly generated. Most widely used due to simplicity and privacy. No correlation between UUIDs.</li>
            <li><strong>UUID v5:</strong> Generated from namespace and name using SHA-1. Same inputs always produce the same UUID.</li>
            <li><strong>UUID v6:</strong> Improved version of v1 with reordered timestamp for better database performance.</li>
            <li><strong>UUID v7:</strong> Unix timestamp-based with random data. Time-ordered and suitable for distributed systems.</li>
          </ul>
        </div>
      </div>

      <style>{`
        .uuid-generator {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .uuid-version-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .version-button {
          background: var(--bg-secondary);
          border: 2px solid var(--border-color);
          border-radius: 12px;
          padding: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: left;
        }

        .version-button:hover {
          border-color: var(--accent-primary);
          transform: translateY(-2px);
        }

        .version-button.active {
          background: var(--accent-light);
          border-color: var(--accent-primary);
        }

        .version-title {
          font-size: 1rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 0.25rem;
        }

        .version-description {
          font-size: 0.8125rem;
          color: var(--text-secondary);
          line-height: 1.4;
        }

        .version-note {
          margin-top: 0.75rem;
          padding: 0.75rem;
          background: var(--accent-light);
          border-left: 3px solid var(--accent-primary);
          border-radius: 6px;
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .v5-options {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1rem;
          background: var(--bg-secondary);
          border-radius: 12px;
        }

        .input-field {
          width: 100%;
          padding: 0.75rem 1rem;
          background: var(--bg-primary);
          border: 2px solid var(--border-color);
          border-radius: 10px;
          font-size: 0.9375rem;
          color: var(--text-primary);
          transition: all 0.3s ease;
        }

        .input-field:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px var(--accent-light);
        }

        .uuid-results {
          margin-top: 1rem;
        }

        .results-header {
          margin-bottom: 1rem;
        }

        .results-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--text-primary);
        }

        .uuid-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .uuid-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 1rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .uuid-item:hover {
          border-color: var(--accent-primary);
        }

        .uuid-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
          overflow: hidden;
        }

        .uuid-value {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.875rem;
          color: var(--accent-primary);
          word-break: break-all;
        }

        .uuid-meta {
          font-size: 0.75rem;
          color: var(--text-tertiary);
        }

        .copy-button {
          flex-shrink: 0;
          padding: 0.5rem;
          background: var(--accent-light);
          border: 1px solid var(--accent-primary);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          color: var(--accent-primary);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .copy-button:hover {
          background: var(--accent-primary);
          color: white;
        }

        .info-section {
          margin-top: 1rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 1px solid var(--border-color);
        }

        .info-title {
          font-size: 1.125rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .info-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .info-list li {
          padding-left: 1.5rem;
          position: relative;
          color: var(--text-secondary);
          font-size: 0.9375rem;
          line-height: 1.6;
        }

        .info-list li::before {
          content: '→';
          position: absolute;
          left: 0;
          color: var(--accent-primary);
          font-weight: bold;
        }

        @media (max-width: 768px) {
          .uuid-version-grid {
            grid-template-columns: 1fr;
          }

          .uuid-item {
            flex-direction: column;
            align-items: stretch;
          }

          .copy-button {
            width: 100%;
          }
        }
      `}</style>
    </ToolLayout>
  );
};

export default UuidGenerator;